/**
 * Shared map tile provider configuration.
 *
 * Used by all geo-map components (GeoModalContent, GeoIpExpansionPanel,
 * GeoPlaybackHistory, ItemGeoMap, VpnGeoMap) to avoid duplicating URLs
 * and tile settings across files.
 */

export type TileProvider = "dark" | "light" | "street" | "satellite";

export type TileConfig = {
  url: string;
  subdomains?: string;
  maxZoom: number;
  label: string;
  icon: string;
};

export const TILE_PROVIDERS: Record<TileProvider, TileConfig> = {
  dark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    subdomains: "abcd",
    maxZoom: 19,
    label: "Dark",
    icon: "lucide:moon",
  },
  light: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    subdomains: "abcd",
    maxZoom: 19,
    label: "Light",
    icon: "lucide:sun",
  },
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: "abc",
    maxZoom: 19,
    label: "Street",
    icon: "lucide:map",
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 18,
    label: "Satellite",
    icon: "lucide:satellite",
  },
};

export const TILE_KEYS = Object.keys(TILE_PROVIDERS) as TileProvider[];

/** Read a persisted tile choice from localStorage, falling back to "dark". */
export const getStoredTile = (storageKey: string): TileProvider => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored && stored in TILE_PROVIDERS) return stored as TileProvider;
  } catch { /* ignore */ }
  return "dark";
};

/** Persist tile choice to localStorage. */
export const storeTile = (storageKey: string, tile: TileProvider): void => {
  try {
    localStorage.setItem(storageKey, tile);
  } catch { /* ignore */ }
};
